import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "menu", "options", "trigger", "triggerLabel", "hiddenInput"]
  static values = {
    options: Array,
    selected: String,
    placeholder: String,
    emptyLabel: { type: String, default: "No matching options." },
    defaultGroup: { type: String, default: "Options" }
  }

  connect() {
    this.renderSelected()
    this.renderOptions()
    this.setExpanded(false)
    document.addEventListener("click", this.closeIfOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.closeIfOutside)
  }

  closeIfOutside = (event) => {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  toggle(event) {
    event.preventDefault()

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  filter() {
    this.renderOptions()
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      this.triggerTarget.focus()
    }
  }

  select(event) {
    event.preventDefault()

    const id = event.currentTarget.dataset.optionId
    const option = this.optionsValue.find((item) => item.id === id)
    if (!option) return

    this.selectedValue = option.id
    this.renderSelected()
    this.renderOptions()
    this.dispatch("change", { detail: { selected: option } })
    this.close()
    this.triggerTarget.focus()
  }

  open() {
    this.menuTarget.classList.remove("hidden")
    this.setExpanded(true)
    this.renderOptions()
    requestAnimationFrame(() => this.inputTarget.focus())
  }

  close() {
    this.menuTarget.classList.add("hidden")
    this.setExpanded(false)
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }
    this.renderOptions()
  }

  renderSelected() {
    const selected = this.selectedOption

    this.triggerLabelTarget.textContent = selected?.name || this.placeholderValue
    this.triggerLabelTarget.classList.toggle("work-builder-picker__trigger--selected", Boolean(selected))
    this.triggerLabelTarget.classList.toggle("work-builder-picker__trigger--placeholder", !selected)
    this.hiddenInputTarget.value = selected?.id || ""
  }

  renderOptions() {
    const query = this.hasInputTarget ? this.inputTarget.value.toLowerCase().trim() : ""
    const options = this.optionsValue.filter((option) => {
      if (query.length === 0) return true

      return [
        option.name,
        option.group,
        option.id
      ].some((value) => value?.toLowerCase().includes(query))
    })

    if (options.length === 0) {
      this.optionsTarget.innerHTML = `
        <div class="work-builder-picker__empty">
          ${this.escapeHtml(this.emptyLabelValue)}
        </div>
      `
      return
    }

    this.optionsTarget.innerHTML = this.groupOptions(options).map(([group, groupOptions]) => `
      <div class="work-builder-picker__group">
        <div class="work-builder-picker__group-label">
          ${this.escapeHtml(group)}
        </div>
        ${groupOptions.map((option) => {
          const selected = option.id === this.selectedValue

          return `
            <button type="button"
                    class="work-builder-picker__option${selected ? " work-builder-picker__option--selected" : ""}"
                    data-action="searchable-select#select"
                    data-option-id="${this.escapeHtml(option.id)}">
              <span class="work-builder-picker__option-content">
                <span class="block text-sm font-medium text-[var(--color-ink)]">${this.escapeHtml(option.name)}</span>
                ${selected ? '<i class="ph ph-check text-[0.75rem]" aria-hidden="true"></i>' : ""}
              </span>
            </button>
          `
        }).join("")}
      </div>
    `).join("")
  }

  groupOptions(options) {
    const groups = new Map()

    options.forEach((option) => {
      const group = option.group || this.defaultGroupValue
      groups.set(group, [...(groups.get(group) || []), option])
    })

    return Array.from(groups.entries())
  }

  setExpanded(expanded) {
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
    }
  }

  get selectedOption() {
    return this.optionsValue.find((option) => option.id === this.selectedValue)
  }

  get isOpen() {
    return !this.menuTarget.classList.contains("hidden")
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text ?? ""
    return div.innerHTML
  }
}
